import {type FC, useMemo} from 'react';

import {useI18n} from '@yourcoach/i18n';

import type {CourseExpandedCommon} from '../../../../api/course';
import type {GetComponentProps} from '../../../../utils/utility-types';
import type {CoachCoursesStore} from '../../stores/CoachCourses';

import {CourseModel} from '../../../../utils/entity/course/createCourseModel';
import {platform} from '../../../../utils/platform';
import {CourseListItemJSX} from '../../components/CourseListItem';

type Props = {
  course: CourseExpandedCommon;
  onClick: (course: CourseExpandedCommon) => void;
  store: CoachCoursesStore;
};

export const CoachCourseListItem: FC<Props> = ({course, onClick, store}) => {
  const {t} = useI18n();
  const model = useMemo(() => new CourseModel(course), [course]);

  const buttons: GetComponentProps<typeof CourseListItemJSX>['buttons'] = [];

  if (model.canBeStarted) {
    buttons.push({
      onClick: () => store.startCourse(course),
      text: t('shared:start'),
    });
  }

  if (model.canBeStopped) {
    buttons.push({
      onClick: () => store.stopCourse(course),
      text: t('shared:end'),
    });
  }

  if (platform.isWeb && model.isDeletable) {
    buttons.push({
      onClick: () => store.deleteCourse(course),
      scheme: 'danger',
      text: t('shared:delete'),
    });
  }

  return (
    <CourseListItemJSX
      buttons={buttons}
      client={
        model.isIndividual
          ? {avatarUrl: model.clientAvatarUrl, name: model.clientName}
          : undefined
      }
      clientsCount={model.clientsCount}
      groupSize={model.groupSize}
      isArchived={model.isArchived}
      isCoachLayout
      onClick={() => onClick(course)}
      onDeleteClick={
        platform.isNative && model.isDeletable
          ? () => store.deleteCourse(course)
          : undefined
      }
      onUpdateClick={
        model.isCourseUpdatable ? () => store.updateCourse(course) : undefined
      }
      status={model.statusString}
      subtitle={model.hasCustomLabel ? model.durationString : undefined}
      title={model.label}
    />
  );
};
